<template>
  <div
    v-if="experimentActive && recommendedPackage"
    :class="$style.container"
  >
    <div :class="[$style.colorPrimary, $style.marginBottom, $style.recommendedPackageContainer]">
      <CustomIcon
        :class="$style.colorPrimary"
        :scale="1"
      >
        <HighlightTagIcon />
      </CustomIcon>
      <HeadingExtraSmall
        :class="$style.recommendedPackageText"
        :hasMargin="false"
      >
        Recommended Package
      </HeadingExtraSmall>
    </div>
    <HeadingLarge
      :class="$style.marginBottom"
      :hasMargin="false"
    >
      {{ recommendedPackage }}
    </HeadingLarge>
    <ParagraphBody
      bodySize="small"
      :class="$style.marginBottom"
      :hasMargin="false"
    >
      We recommend this package based on your feature&nbsp;selections.
    </ParagraphBody>
    <Button
      text="Go To Pricing"
      textCase="lowercase"
      @click="handleGoToPricingClick"
    />
  </div>
</template>

<script>
import {
  Button, CustomIcon, HeadingExtraSmall, HeadingLarge, ParagraphBody,
} from '@jumpcloud/ui-components';
import {
  ItemTypeConsts,
  ItemTypeRoutes,
  StoreModuleMap,
} from 'client/util/Constants';
import { UiEvents } from '@jumpcloud/ui-events-vue';
import { experiments } from 'client/GrowthEngineering/OptimizelyApi';
import { implementationChecklistRegion } from 'client/components/ImplementationChecklist/ImplementationChecklistConstants';
import { mapActions, mapGetters } from 'vuex';
import {
  onboardingFeatures,
} from 'client/GrowthEngineering/utilities/GrowthConstants';
import HighlightTagIcon from 'public/img/Payments/highlight-tag.svg?inline';
import OptimizelyHelper from 'client/GrowthEngineering/OptimizelyHelper';
import SubscriptionConstants from 'client/Payments/pricingMatrix/SubscriptionConstants';
import Util from 'client/util/Util';

const { recommendPackageInChecklist } = experiments;

const {
  appleMdm,
  ssoAuth,
  ldap,
  mfa,
  radius,
  compliance,
  userManagement,
  deviceManagement,
  serverManagement,
} = onboardingFeatures;

const { SubscriptionTypes, getProductCodeByType } = SubscriptionConstants;

const onboardingModule = 'OnboardingModule';
const subscriptionCollection = StoreModuleMap[ItemTypeConsts.subscription];

export default {
  name: 'RecommendedPackage',

  Util,

  components: {
    HeadingLarge,
    HeadingExtraSmall,
    ParagraphBody,
    Button,
    HighlightTagIcon,
    CustomIcon,
  },

  data() {
    return {
      experimentActive: false,
    };
  },

  computed: {
    ...mapGetters(onboardingModule, {
      checklists: 'getImplementationChecklist',
    }),

    ...mapGetters(subscriptionCollection, {
      activeSubscriptions: 'getCollection',
    }),

    pricingLink() {
      return ItemTypeRoutes[ItemTypeConsts.pricing];
    },

    recommendedPackage() {
      if (!this.activeSubscriptions || this.activeSubscriptions.length === 0) return '';

      const subscriptionType = this.getRecommendedSubscriptionType();
      const recommendedPackage = this.activeSubscriptions
        .find(subscription => subscription.productCode === getProductCodeByType(subscriptionType));

      return recommendedPackage ? recommendedPackage.displayName : '';
    },

    selectedFeatures() {
      return this.checklists
        .map(checklist => checklist.feature);
    },
  },

  created() {
    this.experimentActive = OptimizelyHelper.getExperimentVariation(recommendPackageInChecklist);
  },

  mounted() {
    this.fetchCollection();
  },

  methods: {
    ...mapActions(subscriptionCollection, ['fetchCollection']),

    checklistIncludes(features) {
      return features.some(feature => this.selectedFeatures.includes(feature));
    },

    getRecommendedSubscriptionType() {
      if (this.checklistIncludes([mfa])) return SubscriptionTypes.platformPlus;
      if (this.checklistIncludes([
        deviceManagement, appleMdm, serverManagement, compliance,
      ])) return SubscriptionTypes.platform;
      if (this.checklistIncludes([radius, ldap])) return SubscriptionTypes.coreDirectory;
      if (this.checklistIncludes([ssoAuth, userManagement])) return SubscriptionTypes.sso;
      return '';
    },

    handleGoToPricingClick() {
      UiEvents.triggerButtonClicked({
        description: 'go to pricing button clicked from checklist',
        text: 'go to pricing',
        page: this.$page,
        region: implementationChecklistRegion,
        section: 'Recommended Package',
        value: `Package Description: ${this.recommendedPackage}`,
      });
      this.$options.Util.redirect(this.pricingLink);
    },
  },
};

</script>

<style module scoped>
.container {
  margin-top: 3rem;
  text-align: center;
}

.recommendedPackageContainer {
  align-items: center;
  display: flex;
  justify-content: center;
}

.recommendedPackageText.recommendedPackageText {
  display: inline-block;
  margin-left: var(--jc-spacer-small);
  text-transform: uppercase;
}

.marginBottom.marginBottom {
  margin-bottom: 0.75rem;
}

.colorPrimary {
  color: var(--jcPrimary);
}

.colorPrimary path {
  fill: var(--jcPrimary);
}
</style>
